import { isFunction } from "../../data/utils.js";
import { getTypeByName, Kinds } from "../../data/model/data-types.js";
import { Services } from "../../services/Services.js";
import { generateUid } from "../../utils/uid.js";
import { CheckBox } from "../controls/CheckBox.js";
import { ComboBox } from "../controls/ComboBox.js";
import { DatetimePicker } from "../controls/DatetimePicker.js";
import { DurationBox } from "../controls/DurationBox.js";
import { Grid } from "../controls/Grid.js";
import { Group } from "../controls/Group.js";
import { Label } from "../controls/Label.js";
import { NumBox } from "../controls/NumBox.js";
import { TextBox } from "../controls/TextBox.js";
import { Field } from "../controls/Field.js";

function resolveValue(value, context = null) {

    if (isFunction(value)) {
        if (context)
            return value.call(context);
        return value();
    }

    return value;

}

function resolveDataContext(parent, dataContext = null) {

    const context = dataContext || {};

    return {
        getData() {
            if (typeof context.getData == 'function')
                return context.getData();
            if (context.hasOwnProperty('data'))
                return resolveValue(context.data, parent || context);
            if (parent && typeof parent.getData == 'function')
                return parent.getData();
            return null;
        },
        getFields() {
            if (typeof context.getFields == 'function')
                return context.getFields();
            if (context.hasOwnProperty('fields'))
                return resolveValue(context.fields, parent || context) || [];
            if (context.dataModel && typeof context.dataModel.getFields == 'function')
                return context.dataModel.getFields() || [];
            if (parent && typeof parent.getDataModel == 'function') {
                const dataModel = parent.getDataModel();
                return (dataModel && dataModel.getFields && dataModel.getFields()) || [];
            }
            return [];
        },
        getDataModel() {
            if (context.dataModel)
                return context.dataModel;
            if (parent && typeof parent.getDataModel == 'function')
                return parent.getDataModel();
            return null;
        },
        isReadOnly() {
            if (context.hasOwnProperty('isReadOnly'))
                return resolveValue(context.isReadOnly, parent || context) || false;
            if (parent && typeof parent.isReadOnly == 'function')
                return parent.isReadOnly();
            return false;
        },
        onFieldValueChange(value, field, data) {
            if (typeof context.onFieldValueChange == 'function') {
                const result = context.onFieldValueChange(value, field, data);
                return result === false ? false : true;
            }

            if (!data)
                return false;

            if (field && typeof field.changeValue == 'function')
                return field.changeValue(data, value) === false ? false : true;

            if (field && typeof field.setValue == 'function') {
                field.setValue(data, value);
            } else if (field && field.name) {
                data[field.name] = value;
            } else {
                return false;
            }

            data.$changed = true;

            if (parent && typeof parent.invalidate == 'function')
                parent.invalidate();

            return true;
        }
    };

}

function createEditorDefFromField(field) {

    const editorDef = {
        field,
        fieldName: field.name || '?',
        label: field.label,
        type: field.type,
        datatype: field.datatype || null,
        width: field.width || 12,
        visible: true,
        readOnly: field.readOnly || false,
        multiline: field.multiline || false,
        expandable: field.expandable || false,
        minValue: field.minValue,
        maxValue: field.maxValue,
        minLength: field.minLength,
        maxLength: field.maxLength,
        group: {
            width: field.width || 12,
        },
    };

    if (field.format !== undefined && field.format !== null)
        editorDef.format = field.format;
    if (field.availableValues !== undefined && field.availableValues !== null)
        editorDef.items = field.availableValues;

    return editorDef;

}

function resolveFieldFromReference(dataModel, fieldReference) {

    if (!fieldReference)
        return null;

    if (typeof fieldReference == 'object')
        return fieldReference;

    const fields = (dataModel && dataModel.getFields && dataModel.getFields()) || [];
    const field = fields.find((item) => item && item.name == fieldReference) || null;

    if (!field)
        throw new Error(`[data-editor-helpers] Unknown field "${fieldReference}".`);

    return field;

}

function createEditorDefsFromFields(fields = [], options = {}) {

    const include = options.include || ((field) => field && (field.editable || field.inEditor));

    return (fields || [])
        .filter((field) => include(field))
        .map((field) => createEditorDefFromField(field));

}

function createEditorDefsFromModel(dataModel, options = {}) {

    const fields = (options.fields || (dataModel && dataModel.getFields && dataModel.getFields()) || []);
    return createEditorDefsFromFields(fields, options);

}

function createEditorDefsFromFieldNames(dataModel, fieldNames = [], options = {}) {

    const fields = (fieldNames || []).map((fieldReference) => resolveFieldFromReference(dataModel, fieldReference));
    return createEditorDefsFromFields(fields, options);

}

function createEditor(editorDef, parent, dataContext = null, options = {}) {

    const config = Services.get('config');
    const i18n = Services.get('i18n');
    const def = Object.assign({}, editorDef || {}, options || {});
    const field = def.field || null;
    const context = resolveDataContext(parent, dataContext);

    let controlOptions = {};

    controlOptions.id = def.id || `${def.randomizeId ? generateUid() : 'ui'}-${(def.fieldName || 'field').toLowerCase()}-editor`;
    controlOptions.value = def.value || function () {
        const data = context.getData();
        if (!data || !field)
            return null;
        return field.getValue(data);
    };
    if (def.valueDefault !== undefined)
        controlOptions.valueDefault = def.valueDefault;
    if (def.valueFrom !== undefined)
        controlOptions.valueFrom = def.valueFrom;
    if (def.valueTo !== undefined)
        controlOptions.valueTo = def.valueTo;
    controlOptions.onValueChange = def.onValueChange || function (value) {
        return context.onFieldValueChange(value, field, context.getData());
    };
    controlOptions.valueValidations = def.valueValidations || function () {
        return (field && field.getValidations && field.getValidations()) || [];
    };

    if (typeof def.afterValueChange == 'function') {
        const changeHandler = controlOptions.onValueChange;
        controlOptions.onValueChange = (value) => {
            if (changeHandler(value) === false)
                return false;
            def.afterValueChange(value, field);
            return true;
        };
    }

    controlOptions.immediateValueChange = def.immediateValueChange || false;
    controlOptions.readOnly = (typeof def.readOnly == 'function')
        ? function () { return def.readOnly(); }
        : function () { return def.readOnly ?? context.isReadOnly(); };
    controlOptions.visible = def.visible ?? true;
    controlOptions.width = def.width || (field && field.width) || 12;
    controlOptions.autofocus = def.autofocus || false;

    for (const optionName of ['minValue', 'maxValue', 'minLength', 'maxLength']) {
        if (def[optionName] !== undefined)
            controlOptions[optionName] = def[optionName];
    }

    const datatype = def.datatype || (field && field.datatype) || getTypeByName(def.type || (field && field.type));

    switch (datatype.kind) {

        case Kinds.Number:
            if (def.format !== undefined && def.format !== null)
                controlOptions.format = def.format;
            controlOptions.locale = def.locale || (() => (parent && parent.getApp && parent.getApp()) ? parent.getApp().getLocale() : config.locale);
            return new NumBox(parent, controlOptions);

        case Kinds.String: 
            controlOptions.multiline = def.multiline || false;
            controlOptions.expandable = def.expandable || false;
            return new TextBox(parent, controlOptions);

        case Kinds.Ref:
            controlOptions.items = def.items || [];
            controlOptions.itemValueProp = def.itemValueProp || 'value';
            controlOptions.itemLabelProp = def.itemLabelProp || 'label';
            controlOptions.itemDisabledProp = def.itemDisabledProp || null;
            controlOptions.valueContainsItem = false;
            controlOptions.emptyOptionValue = def.emptyOptionValue ?? null;
            controlOptions.emptyOptionLabel = def.emptyOptionLabel || i18n.t('control.combobox.empty_value.select');
            controlOptions.onGetOptionValue = def.onGetOptionValue || null;
            controlOptions.onGetOptionLabel = def.onGetOptionLabel || null;
            return new ComboBox(parent, controlOptions);

        case Kinds.Datetime:
            if (def.format !== undefined && def.format !== null)
                controlOptions.format = def.format;
            if (def.extraFormats)
                controlOptions.extraFormats = def.extraFormats;
            controlOptions.locale = def.locale || (() => (parent && parent.getApp && parent.getApp()) ? parent.getApp().getLocale() : config.locale);
            return new DatetimePicker(parent, controlOptions);

        case Kinds.Duration:
            if (def.format !== undefined && def.format !== null)
                controlOptions.format = def.format;
            controlOptions.locale = def.locale || (() => (parent && parent.getApp && parent.getApp()) ? parent.getApp().getLocale() : config.locale);
            return new DurationBox(parent, controlOptions);

        case Kinds.Bool:
            return new CheckBox(parent, controlOptions);

    }

    return null;

}

function createEditors(editorDefs = [], parent, dataContext = null) {

    return (editorDefs || [])
        .map((editorDef) => createEditor(editorDef, parent, dataContext))
        .filter((editor) => !!editor);

}

function createEditorFromField(parent, field, options = {}, dataContext = null) {

    const editorDef = Object.assign({}, createEditorDefFromField(field), options || {});
    return createEditor(editorDef, parent, dataContext);

}

function createEditorFromModelField(parent, dataModel, fieldName, options = {}, dataContext = null) {

    const fields = (dataModel && dataModel.getFields && dataModel.getFields()) || [];
    const field = fields.find((item) => item.name == fieldName);

    if (!field)
        throw new Error(`The field "${fieldName}" not found in data model.`);

    return createEditorFromField(parent, field, options, dataContext);

}

function createEditorGroup(editorDef, parent, dataContext = null, groupOptions = {}) {

    const editor = createEditor(editorDef, parent, dataContext);

    if (!editor)
        return null;

    const def = Object.assign({}, editorDef || {});
    const dataField = def.field || null;
    const defGroup = Object.assign({}, def.group || {}, groupOptions || {});
    const labelText = defGroup.label || def.label || (dataField && dataField.label) || '';
    const width = dataField.controlWidth ?? def.width ?? 12;

    if (defGroup.grid) {
        const grid = new Grid(parent, {
            visible: dataField.visible ?? def.visible ?? true,
        });

        if (dataField.hideLabel) {
            grid.cell({
                css: ct.width(width),
                controls: [editor]
            });
        }

        const field = new Field(grid, {
            id: dataField.id || null,
            label: labelText,
            css: '.field',
            control: editor
        });

        grid.cell({ controls: [field] });

        return grid;
    }

    const controls = [];

    if (!defGroup.hideLabel) {
        controls.push(new Label(parent, {
            id: defGroup.id || null,
            for: editor.id || null,
            text: labelText,
        }));
    }

    controls.push(editor);

    return new Group(parent, {
        width: defGroup.width || def.width || (dataField && dataField.width) || 12,
        visible: defGroup.visible ?? def.visible ?? true,
        controls,
    });

}

function createEditorGroups(editorDefs = [], parent, dataContext = null, groupOptions = {}) {

    let index = 0;

    return (editorDefs || []).reduce((controls, editorDef) => {
        const actualGroupOptions = Object.assign({}, groupOptions || {});

        if (index === 0 && actualGroupOptions.autofocus === undefined)
            editorDef = Object.assign({}, editorDef, { autofocus: true });

        const control = createEditorGroup(editorDef, parent, dataContext, actualGroupOptions);

        if (control) {
            controls.push(control);
            index++;
        }

        return controls;
    }, []);

}

function createEditorGroupsFromFields(parent, fields = [], dataContext = null, groupOptions = {}, options = {}) {

    const editorDefs = createEditorDefsFromFields(fields, options);
    return createEditorGroups(editorDefs, parent, dataContext, groupOptions);

}

function createEditorGroupsFromModel(parent, dataModel, dataContext = null, groupOptions = {}, options = {}) {

    const editorDefs = createEditorDefsFromModel(dataModel, options);
    return createEditorGroups(editorDefs, parent, dataContext, groupOptions);

}

export {
    createEditor,
    createEditors,
    createEditorDefFromField,
    createEditorDefsFromFields,
    createEditorDefsFromFieldNames,
    createEditorDefsFromModel,
    createEditorFromField,
    createEditorFromModelField,
    createEditorGroup,
    createEditorGroups,
    createEditorGroupsFromFields,
    createEditorGroupsFromModel,
};
